// src/pages/scheduler/SchedulerBuildGuide.tsx — the in-app "Build guide" tab.
// Port of the prototype scheduler-devguide.jsx; mirrors SCHEDULER_FRAMEWORK_SPEC.md
// so any future session can wire a new trigger correctly. Static reference.

import type { CSSProperties, ReactNode } from "react";
import { C, F } from "../../theme";
import * as Icons from "../../components/ui/Icon";
import { Eyebrow, Pill, Stamp, iconByKey } from "./kit";
import { Block } from "./SchedulerDetail";
import { CONTROL_TYPES } from "./types";

const mono: CSSProperties = { fontFamily: F.mono };

function Code({ children }: { children: ReactNode }) {
  return (
    <pre style={{ background: "#2B2417", border: `1px solid ${C.brassDk}`, borderRadius: 13, padding: "14px 16px", margin: 0, overflowX: "auto", fontFamily: F.mono, fontSize: 12.5, lineHeight: 1.6, color: "#E9D9A8", boxShadow: "inset 0 1px 0 rgba(243,220,154,0.08)" }}>{children}</pre>
  );
}
const Kw = ({ children }: { children: ReactNode }) => <span style={{ color: "#C6B0D2" }}>{children}</span>;
const Str = ({ children }: { children: ReactNode }) => <span style={{ color: "#A7C77F" }}>{children}</span>;
const Cm = ({ children }: { children: ReactNode }) => <span style={{ color: "rgba(243,220,154,0.45)" }}>{children}</span>;

function Rule({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 13, padding: "12px 0", borderTop: n === 1 ? "none" : `1px solid ${C.hairlineLt}` }}>
      <div style={{ width: 30, height: 30, borderRadius: 9, background: C.brassDk, color: C.brassGlow, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: F.mono, fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{n}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: F.body, fontSize: 14.5, fontWeight: 800, color: C.ink, lineHeight: 1.3 }}>{title}</div>
        <div style={{ fontSize: 13, color: C.inkSoft, fontWeight: 600, marginTop: 3, lineHeight: 1.5 }}>{children}</div>
      </div>
    </div>
  );
}

function SchedRow({ ic, name, desc, shape }: { ic: string; name: string; desc: string; shape: string }) {
  const Ic = iconByKey(ic);
  return (
    <div style={{ border: `1px solid ${C.hairlineLt}`, borderRadius: 13, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "11px 13px", background: C.paperSoft }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: C.brassBg, border: `1px solid ${C.brassLt}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Ic s={16} c={C.brassDk} /></div>
        <code style={{ ...mono, fontSize: 13, fontWeight: 700, color: C.brassDk, flexShrink: 0 }}>{name}</code>
        <span style={{ fontSize: 13, color: C.inkSoft, fontWeight: 600 }}>{desc}</span>
      </div>
      <div style={{ padding: "9px 13px", background: C.paperWarm, borderTop: `1px solid ${C.hairlineLt}` }}>
        <code style={{ ...mono, fontSize: 11.5, color: C.inkSoft, fontWeight: 600, lineHeight: 1.5, wordBreak: "break-word" }}>{shape}</code>
      </div>
    </div>
  );
}

export function BuildGuide() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <section style={{ background: C.card, borderRadius: 18, border: `1px solid ${C.hairlineLt}`, boxShadow: C.shadowM, overflow: "hidden" }}>
        <div style={{ height: 3, background: `linear-gradient(90deg,${C.heatherDk},transparent 75%)` }} />
        <div style={{ padding: "16px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: C.heatherBg, border: `1px solid ${C.hairlineLt}`, display: "flex", alignItems: "center", justifyContent: "center" }}><Icons.Code s={20} c={C.heatherDk} /></div>
            <div>
              <Eyebrow c={C.heatherDk}>For Claude Code</Eyebrow>
              <div style={{ fontFamily: F.serif, fontSize: 21, fontWeight: 700, color: C.ink, letterSpacing: -0.3, lineHeight: 1.05 }}>How to wire a new trigger</div>
            </div>
          </div>
          <p style={{ fontSize: 14.5, color: C.inkSoft, fontWeight: 600, lineHeight: 1.55, margin: 0 }}>
            The heartbeat runs every registered job on its schedule. Info Hub is the universal control surface — it renders <b>any</b> trigger that follows this contract, with zero bespoke UI per applet. To add a job, register one object in the code registry (api/src/scheduler-registry.ts), add a handler in the dispatcher's map, and seed a row in a migration. Don't build your own settings screen — declare your tunable controls and they appear here automatically.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
            <Pill tone="heather" small>One object per job</Pill>
            <Pill tone="brass" small>Declare config, don't draw it</Pill>
            <Pill tone="sage" small>Friendly schedule presets</Pill>
            <Pill tone="paper" small>Timing is generated for you</Pill>
          </div>
        </div>
      </section>

      <Block icon="Star" eyebrow="Read first" title="Golden rules">
        <div style={{ paddingTop: 4 }}>
          <Rule n={1} title="Register, don't render.">Append a trigger to the registry. Info Hub draws the card, detail screen, history and every config control from your data. Never hand-build a settings UI for a scheduled job.</Rule>
          <Rule n={2} title="Namespace the id as app.job.">e.g. <code style={mono}>sign-in.lone-working-due</code>. The part before the dot must match an <code style={mono}>app.key</code> so it lands in the right group.</Rule>
          <Rule n={3} title="Write the descriptions for a non-technical manager.">The operator runs the railway, not the code. <code style={mono}>description</code> is plain English; <code style={mono}>does</code> is a one-line mechanism note.</Rule>
          <Rule n={4} title="Expose only what's safe to tune.">Every entry in <code style={mono}>config</code> becomes a live control. Keep secrets and wiring in code. If a threshold already lives in System Preferences, don't duplicate it here.</Rule>
          <Rule n={5} title="Always fill impact.">Audience, systems touched, and a plain warning if the job sends messages or changes data. This is how the operator judges risk before flipping the switch.</Rule>
          <Rule n={6} title="Use friendly schedules only.">Pick <code style={mono}>interval</code>, <code style={mono}>time-of-day</code>, or <code style={mono}>on-event</code>. The heartbeat works out the timing — never surface raw cron.</Rule>
        </div>
      </Block>

      <Block icon="Code" eyebrow="The contract" title="Trigger object shape" accent={C.heatherDk}>
        <div style={{ paddingTop: 10 }}>
          <Code>
            <Cm>{"// one entry in scheduler-registry.ts = one scheduled job"}</Cm>{"\n"}
            {"{\n"}
            {"  "}<Kw>id</Kw>{": "}<Str>"app-key.job-name"</Str>{",        "}<Cm>{"// unique, namespaced"}</Cm>{"\n"}
            {"  "}<Kw>app</Kw>{": "}<Str>"app-key"</Str>{",               "}<Cm>{"// must match an app in APPS"}</Cm>{"\n"}
            {"  "}<Kw>name</Kw>{": "}<Str>"Human Readable Name"</Str>{",\n"}
            {"  "}<Kw>action</Kw>{": "}<Str>"Sends text"</Str>{", "}<Kw>cadence</Kw>{": "}<Str>"Every minute"</Str>{",\n"}
            {"  "}<Kw>defaultSchedule</Kw>{": { "}<Kw>type</Kw>{": "}<Str>"interval"</Str>{", "}<Kw>interval</Kw>{": { "}<Kw>every</Kw>{": 5, "}<Kw>unit</Kw>{": "}<Str>"minutes"</Str>{" } },\n"}
            {"  "}<Kw>summary</Kw>{": "}<Str>"One-liner."</Str>{", "}<Kw>description</Kw>{": "}<Str>"Paragraph."</Str>{", "}<Kw>does</Kw>{": "}<Str>"Mechanism."</Str>{",\n"}
            {"  "}<Kw>config</Kw>{": [ "}<Cm>{"/* control defs — see catalogue */"}</Cm>{" ],\n"}
            {"  "}<Kw>impact</Kw>{": { "}<Kw>severity</Kw>{": "}<Str>"high"</Str>{", "}<Kw>audience</Kw>{": "}<Str>"…"</Str>{", "}<Kw>systems</Kw>{": […] },\n"}
            {"  "}<Kw>version</Kw>{": "}<Str>"1.0.0"</Str>{", "}<Kw>changelog</Kw>{": [ … ],\n"}
            {"}"}
          </Code>
        </div>
      </Block>

      <Block icon="Sliders" eyebrow="Building blocks" title="Config control catalogue" accent={C.heatherDk} right={<Stamp tone="heather">{CONTROL_TYPES.length} types</Stamp>}>
        <div style={{ paddingTop: 4 }}>
          {CONTROL_TYPES.map((ct, i) => (
            <div key={ct.type} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 0", borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}` }}>
              <code style={{ ...mono, fontSize: 12.5, fontWeight: 700, color: C.heatherDk, background: C.heatherBg, padding: "3px 9px", borderRadius: 7, flexShrink: 0, minWidth: 96, textAlign: "center" }}>{ct.type}</code>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>{ct.label}</div>
                <code style={{ ...mono, fontSize: 12, color: C.inkSoft, fontWeight: 600, lineHeight: 1.4 }}>{ct.note}</code>
              </div>
            </div>
          ))}
        </div>
      </Block>

      <Block icon="Clock" eyebrow="Pick one" title="Schedule presets">
        <div style={{ paddingTop: 6, display: "flex", flexDirection: "column", gap: 10 }}>
          <SchedRow ic="Repeat" name="interval" desc="Fires on a fixed cadence." shape={`{ type:"interval", interval:{ every:30, unit:"minutes" } }`} />
          <SchedRow ic="Clock" name="time-of-day" desc="Fires at set times on chosen days." shape={`{ type:"time-of-day", times:["07:00"], days:["fri"] }`} />
          <SchedRow ic="Lightning" name="on-event" desc="Fired by the app, not the clock." shape={`{ type:"on-event", event:"user.signed-in" }`} />
        </div>
      </Block>
    </div>
  );
}
